const config = require("./memberStampCardConfig.json");
const EXPERIMENT_KEY = "member_stamp_card_v1";
const REWARD_KEY = "MEMBER_STAMP_CARD_V1";
const REWARD_DESCRIPTION = "Punch card: 1-hour free";
const notificationDay = (value) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(value)).map(({ type, value: part }) => [type, part]));
  return `${parts.year}-${parts.month}-${parts.day}`;
};
const normalizeHoleOffset = (offset) => offset && typeof offset === "object" && !Array.isArray(offset) && Number.isInteger(offset.x) && Number.isInteger(offset.y) &&
  Math.hypot(offset.x, offset.y) <= 30 ? { x: offset.x, y: offset.y } : null;
const formatPunchCredit = (hours) => `${hours} ${hours === 1 ? "hour" : "hours"} from your punch card`;
const timestampMillis = (value) => {
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
};
const isConfigured = (settings = config) =>
  settings.treatmentPercent === 50 &&
  timestampMillis(settings.enrollmentStartsAt) > 0 &&
  (!settings.enrollmentEndsAt || timestampMillis(settings.enrollmentEndsAt) > timestampMillis(settings.enrollmentStartsAt));
const isEnabled = (settings = config, now = Date.now()) =>
  isConfigured(settings) &&
  timestampMillis(settings.enrollmentStartsAt) <= now;
const isEligibleMember = (member, settings = config, now = Date.now()) =>
  isEnabled(settings, now) && timestampMillis(member?.created) >= timestampMillis(settings.enrollmentStartsAt) &&
  (!settings.enrollmentEndsAt || timestampMillis(member?.created) < timestampMillis(settings.enrollmentEndsAt)) &&
  timestampMillis(member?.created) <= now;
const buildExperiment = (settings = config) => ({
  key: EXPERIMENT_KEY,
  revisionId: settings.revisionId,
  status: isConfigured(settings) ? "active" : "paused",
  effectiveStartAt: settings.enrollmentStartsAt || "2099-01-01T08:00:00.000Z",
  title: "Member punch card",
  description: "One welcome punch and four completed visits unlock an hour credit.",
  hypothesis: "A tangible head start toward an hour credit increases first and repeat visits.",
  kind: "ab_test", owner: "growth", surfaceSlot: "member_stamp_card",
  surfaces: ["member_stamp_card"],
  referenceVariantKey: "control", fallbackVariantKey: "control",
  assignmentMetricKey: "assignment", exposureMetricKey: "stamp_card_eligible",
  primaryMetricKey: "outcome_member_visit_booked",
  primaryMetricLabel: "Visit bookings per assigned member",
  northStarMetricKey: "total_gross_profit_d90_per_assigned_member",
  assignment: {
    unit: "member", cadence: "static", seedSalt: settings.revisionId, revisionId: settings.revisionId,
    eligibilityPolicyKey: "universal",
    assignmentHashVersion: "uint53_avalanche_v1", assignmentBucketPolicyVersion: "cadence_weighted_v1",
    variants: [{ key: "control", weight: 100 - settings.treatmentPercent }, { key: "stamp_card", weight: settings.treatmentPercent }],
  },
  variants: [{ key: "control", label: "Existing experience", payload: {} }, { key: "stamp_card", label: "Punch card", payload: {} }],
  metrics: [
    { key: "stamp_card_eligible", label: "Eligible member", kind: "exposure" },
    { key: "stamp_card_opened", label: "Card opened", kind: "view" },
    { key: "stamp_card_punched", label: "Punch claimed", kind: "action" },
    { key: "stamp_card_reward_earned", label: "Hour credit earned", kind: "conversion" },
  ],
  valueMetrics: {
    enabled: isConfigured(settings),
    disabledReason: isConfigured(settings) ? null : "feature_off",
    activatedAt: settings.enrollmentStartsAt || "2099-01-01T08:00:00.000Z",
    effectiveStartAt: settings.enrollmentStartsAt || "2099-01-01T08:00:00.000Z",
    decisionGradeStartAt: settings.enrollmentStartsAt || "2099-01-01T08:00:00.000Z",
    comparisonVariantKey: "control", comparisonStatus: "valid_concurrent_control",
    financialPolicy: "modular_clock_horizons",
    financialHorizonKeys: ["d30", "d90", "d365", "lifetime"],
    primaryFinancialHorizonKey: "d90",
  },
});
const isQualifyingVisit = (visit, now = Date.now()) => {
  const flags = Array.isArray(visit?.flags) ? visit.flags : [];
  return visit?.billed === true && (!visit?.status || visit.status === "confirmed") &&
    timestampMillis(visit.completedAt) > 0 && timestampMillis(visit.completedAt) <= now &&
    !visit.cancelledAt && !flags.some((flag) => ["nonBillable", "helperNoShow", "memberCancellation", "chargeCancellationFee", "refundIssued"].includes(flag));
};
const isCardArchived = (card, now = Date.now()) => Number.isFinite(card?.hideAfter) && card.hideAfter > 0 && now >= card.hideAfter;
const projectCard = ({ card = {}, visitIds = [], discount = null }) => {
  const rewarded = Boolean(card.rewardIssuedAt);
  const rewardIssuedAt = rewarded ? timestampMillis(card.rewardIssuedAt) : 0;
  if (rewarded && (typeof card.rewardIssuedAt === "boolean" || !(rewardIssuedAt > 0))) throw new Error("Reward timestamp is invalid");
  const earnedCount = rewarded ? 5 : 1 + Math.min(4, new Set(visitIds).size);
  const claimedCount = rewarded ? 5 : Math.min(earnedCount, Math.max(0, Number.isInteger(card.claimedCount) ? card.claimedCount : 0));
  return { enabled: true, hideAfter: rewarded ? rewardIssuedAt + 7 * 24 * 60 * 60 * 1000 : null, earnedCount, claimedCount, pendingCount: earnedCount - claimedCount,
    holeOffsets: Array.from({ length: claimedCount }, (_, index) => normalizeHoleOffset(card.holeOffsets?.[index])),
    notifiedEarnedCount: Math.max(0, Number(card.notifiedEarnedCount) || 0),
    lastNotifiedDay: /^\d{4}-\d{2}-\d{2}$/.test(card.lastNotifiedDay || "") ? card.lastNotifiedDay : null,
    rewardHoursRemaining: rewarded && !discount?.used ? Math.max(0, Number(discount?.amount) || 0) : 0,
    remainingVisits: 5 - earnedCount, rewardReady: rewarded && Boolean(discount) && !discount.used,
    rewardUsed: rewarded && Boolean(discount?.used) };
};
module.exports = { formatPunchCredit, notificationDay, config, EXPERIMENT_KEY, REWARD_KEY, REWARD_DESCRIPTION, normalizeHoleOffset, timestampMillis, isConfigured, isEnabled, isEligibleMember, buildExperiment, isQualifyingVisit, isCardArchived, projectCard };
